import { differenceInYears } from 'date-fns';
import { hash } from 'ohash';
import { uid } from 'radash';

/**
 *
 * Creates a unique reference for passenger.
 *
 * The passenger "ref" is used everywhere in the app and backend API to uniquely identify a passenger.
 * Whenever data of the passenger changes, the "ref" should be updated.
 *
 * Passengers added to a new product will receive a new "ref", even if the data is the same as a passenger in another product.
 *
 */
export function getPassengerRef(index: number, passenger: Passenger) {
    return `pax-${index}-${hash([
        passenger.id,
        passenger.firstName,
        passenger.lastName,
        passenger.address,
        passenger.photo,
        passenger.dateOfBirth?.getTime(),
        passenger.keycard,
        passenger.reductionCards,
        passenger.promotionCode,
    ]).substring(0, 20)}`;
}

/**
 *
 * Creates a unique UUID for passenger. This UUD never changes, even if the passenger data changes.
 * This is mainly used inside the frontend app, for example as a :key in a v-for loop. It has two purposes:
 * - Have a persistend "id" for a passenger item, that doesn't change when changing data (in contrast to ref)
 * - Have a unique UUID, so a passenger ref is unique, even if chosing the exact same name, birthdate etc. as another passenger.
 */
export function getPassengerUUID() {
    const runtimeConfig = useRuntimeConfig();

    if (runtimeConfig.public.disablePassengerUuid) {
        // This is only for testing purposes, to make sure that the passenger UUID is deterministic.
        console.warn('Passenger UUID is disabled. This should only be enabled for testing purposes.');
        return '12345678901234567890';
    }
    else {
        return uid(20);
    }
}

/**
 * Returns an empty passenger object (e.g. to start a fresh form)
 */
export function getEmptyPassenger(): Passenger {
    return {
        id: getPassengerUUID(),
        ref: '',
        firstName: '',
        lastName: '',
        address: undefined,
        photo: undefined,
        diet: undefined,
        weight: undefined,
        height: undefined,
        shoeSize: undefined,
        skillLevel: undefined,
        dateOfBirth: undefined,
        keycard: undefined,
        reductionCards: [],
        promotionCode: undefined,
    };
}

/**
 * Returns passenger age based at the date of the trip based on the date of birth
 */
export function getPassengerAge(dateOfBirth?: Date, tripDate?: Date): number | undefined {
    if (!dateOfBirth || !tripDate) {
        return undefined;
    }
    return differenceInYears(tripDate, dateOfBirth);
};

/**
 * Returns, if the passenger is a toddler based on the date of birth and a trip date for comparison.
 */
export function isToddler(dateOfBirth?: Date, tripDate?: Date): boolean {
    return (getPassengerAge(dateOfBirth, tripDate) ?? 0) < configPassengerAges.AGE_CHILD;
};

/**
 * Returns, if the passenger is an adult (for accommodations) based on the date of birth and a trip date for comparison.
 */
export function isAccommodationAdult(dateOfBirth?: Date, tripDate?: Date): boolean {
    return (getPassengerAge(dateOfBirth, tripDate) ?? 0) >= configPassengerAges.AGE_HOTEL_ADULT;
};

/**
 * Returns a passenger age description based on the age.
 * @param age
 * @returns text description of the age
 */
export function getPassengerAgeDescription(age: number | undefined, includeList = [
    'toddler',
    'child',
    'teen',
    'adult',
    'senior',
]): string {
    const { t: $t } = useNuxtApp().$i18n;

    if (age !== undefined && age >= configPassengerAges.AGE_SENIOR && includeList.includes('senior')) {
        return $t(configPassengerAgeDescriptions.AGE_SENIOR);
    }
    else if (age !== undefined && age >= configPassengerAges.AGE_ADULT && includeList.includes('adult')) {
        return $t(configPassengerAgeDescriptions.AGE_ADULT);
    }
    else if (age !== undefined && age >= configPassengerAges.AGE_TEEN && includeList.includes('teen')) {
        return $t(configPassengerAgeDescriptions.AGE_TEEN);
    }
    else if (age !== undefined && age >= configPassengerAges.AGE_CHILD && includeList.includes('child')) {
        return $t(configPassengerAgeDescriptions.AGE_CHILD);
    }
    else if (age !== undefined && age < configPassengerAges.AGE_CHILD && includeList.includes('toddler')) {
        return $t(configPassengerAgeDescriptions.AGE_TODDLER);
    }
    return $t(configPassengerAgeDescriptions.AGE_ADULT);
}

export const REDUCTION_CARD_CODE_DEFAULT = 'KEINE_ERMAESSIGUNGSKARTE';

/**
 *
 */
export function getAnonymousPassengerSpecifications(passengerCount: number, passengerAge: number, promotion?: Promotion): ApiSchema['AnonymousPassengerSpecification'][] {
    return Array.from({ length: passengerCount }, (_, i) => ({
        externalRef: `anonymous-${i}`,
        type: 'PERSON',
        age: passengerAge,
        cards: [],
        ...(promotion && promotion.promotionType === 'INDIVIDUAL' && { promotionCode: { code: promotion.code } }),
    }));
}
